import React, { useEffect, useState } from 'react';
import { LiveState, TeamsCaptureStatus } from '../../../types/live';
import MicIcon from '../../icons/MicIcon';
import PlayIcon from '../../icons/PlayIcon';
import StopIcon from '../../icons/StopIcon';

// Which controller operation is currently in flight, so only the affected
// control shows a progress label (M5) instead of a global busy flag disabling
// unrelated controls silently.
export type DockBusyAction = 'start-live' | 'stop-live' | 'transcriber' | 'slide';

interface PresenterDockProps {
  liveState: LiveState;
  busyAction: DockBusyAction | null;
  // Whether a live session may be started for this meeting. Only a Prepared
  // meeting can go live (the backend enforces this with a 409); when false the
  // Start Live control is disabled so a Completed/Processing meeting cannot be
  // (re)started. Defaults to true for backward-compatibility.
  canGoLive?: boolean;
  micSupported: boolean;
  micListening: boolean;
  teamsStatus: TeamsCaptureStatus;
  teamsError: string | null;
  teamsActive: boolean;
  initialJoinUrl?: string | null;
  onStartLive: () => void;
  onStopLive: () => void;
  onToggleMic: () => void;
  onJoinTeams: (joinUrl: string) => void;
  onLeaveTeams: () => void;
}

const teamsStatusLabel: Record<TeamsCaptureStatus, string> = {
  idle: '',
  connecting: 'Connecting to Teams…',
  inLobby: 'In lobby — admit "Meeting Observer" in Teams',
  connected: 'Connected — starting captions…',
  captioning: 'Captioning the Teams meeting',
  error: 'Teams connection error',
};

// The single Secretary presenter dock (H3/M5). Folds the former SessionControls
// and TeamsConnectControl into one hierarchy — Broadcast → Captions source —
// with the danger action (Stop Live) isolated on the right. The captions source
// is an explicit mutually-exclusive choice (Microphone vs Teams); the chosen
// source collapses to a one-line status once connected.
//
// The transcriber auto-starts when a source connects (see LiveMeeting), so there
// is no manual transcriber Start/Stop control here — capture is driven entirely
// by the chosen captions source (Stop mic / Leave Teams).
const PresenterDock: React.FC<PresenterDockProps> = ({
  liveState,
  busyAction,
  canGoLive = true,
  micSupported,
  micListening,
  teamsStatus,
  teamsError,
  teamsActive,
  initialJoinUrl,
  onStartLive,
  onStopLive,
  onToggleMic,
  onJoinTeams,
  onLeaveTeams,
}) => {
  const { is_live } = liveState;

  const [joinUrl, setJoinUrl] = useState(initialJoinUrl || '');
  // Which caption source the controller has picked. Mic and Teams are mutually
  // exclusive; a running source pins the selection so the other is greyed out.
  const [source, setSource] = useState<'mic' | 'teams' | null>(null);

  // Adopt the stored Teams link once it loads (races the live view mount);
  // never clobber user edits.
  useEffect(() => {
    if (initialJoinUrl && !joinUrl) setJoinUrl(initialJoinUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJoinUrl]);

  // Reflect the actual running source back into the selection so a reconnect or
  // an externally-started source shows the right collapsed line.
  useEffect(() => {
    if (teamsActive) setSource('teams');
    else if (micListening) setSource('mic');
  }, [teamsActive, micListening]);

  // Going not-live clears the source selection for the next session.
  useEffect(() => {
    if (!is_live) setSource(null);
  }, [is_live]);

  const startingLive = busyAction === 'start-live';
  const stoppingLive = busyAction === 'stop-live';

  const teamsLabel = teamsStatusLabel[teamsStatus];
  const teamsConnected = teamsStatus === 'connected' || teamsStatus === 'captioning';

  // ---- Not live yet (1a) ----
  if (!is_live) {
    return (
      <div className="presenter-dock">
        <div className="presenter-dock-row">
          <div className="presenter-dock-group">
            <span className="presenter-dock-label">Broadcast</span>
            <span className="presenter-dock-state">
              <span className="presenter-dock-dot presenter-dock-dot-offline" aria-hidden="true" />
              Offline
            </span>
          </div>
          <div className="presenter-dock-group presenter-dock-source-hint">
            <span className="presenter-dock-label">Captions source</span>
            <span className="presenter-dock-muted">Chosen after you go live</span>
          </div>
          <div className="presenter-dock-spacer" />
          <span
            className="live-entry-wrap"
            title={canGoLive ? undefined : 'Meeting must be in Prepared state to go live'}
          >
            <button
              className="meeting-btn meeting-btn-primary presenter-dock-icon-btn"
              disabled={startingLive || !canGoLive}
              onClick={onStartLive}
            >
              {startingLive ? (
                'Starting Live…'
              ) : (
                <>
                  <PlayIcon />
                  Start Live
                </>
              )}
            </button>
          </span>
        </div>
      </div>
    );
  }

  // ---- Live: caption source picker or collapsed status ----
  const renderSource = () => {
    // Collapsed status line once a source is connected/running (1d).
    if (source === 'teams' && teamsActive) {
      return (
        <div className="presenter-dock-source-connected">
          <span className="presenter-dock-label">Captions: Teams</span>
          <span className={`presenter-dock-status${teamsStatus === 'error' ? ' presenter-dock-error' : ''}`}>
            {teamsConnected ? 'Connected' : teamsLabel}
          </span>
          <button
            className="meeting-btn meeting-btn-sm meeting-btn-secondary presenter-dock-leave"
            onClick={onLeaveTeams}
          >
            Leave Teams
          </button>
        </div>
      );
    }
    if (source === 'mic' && micListening) {
      return (
        <div className="presenter-dock-source-connected">
          <span className="presenter-dock-label">Captions: Microphone</span>
          <span className="presenter-dock-status">Listening</span>
          <button
            className="meeting-btn meeting-btn-sm presenter-dock-leave presenter-dock-danger presenter-dock-icon-btn"
            onClick={onToggleMic}
            aria-pressed={true}
            aria-label="Stop microphone capture"
          >
            <StopIcon size={13} />
            Stop mic
          </button>
        </div>
      );
    }

    // Teams selected → link form (1c).
    if (source === 'teams') {
      return (
        <div className="presenter-dock-source-form">
          <span className="presenter-dock-label">Captions source: Teams</span>
          <div className="presenter-dock-teams-row">
            <label className="sr-only" htmlFor="presenter-dock-teams-url">Teams meeting link</label>
            <input
              id="presenter-dock-teams-url"
              className="meeting-form-input teams-connect-input"
              type="url"
              placeholder="https://teams.microsoft.com/l/meetup-join/…"
              value={joinUrl}
              disabled={teamsActive}
              onChange={(e) => setJoinUrl(e.target.value)}
            />
            <button
              className="meeting-btn meeting-btn-sm meeting-btn-secondary"
              disabled={!joinUrl.trim() || teamsStatus === 'connecting'}
              onClick={() => onJoinTeams(joinUrl.trim())}
            >
              {teamsStatus === 'connecting' ? 'Joining Teams…' : 'Join Teams'}
            </button>
            <button
              className="meeting-btn meeting-btn-sm meeting-btn-ghost"
              onClick={() => setSource(null)}
            >
              Cancel
            </button>
          </div>
          {(teamsLabel || teamsError) && (
            <span className={`presenter-dock-status${teamsStatus === 'error' ? ' presenter-dock-error' : ''}`}>
              {teamsStatus === 'error' && teamsError ? `${teamsLabel}: ${teamsError}` : teamsLabel}
            </span>
          )}
        </div>
      );
    }

    // No source chosen yet — the mutually-exclusive picker (1b).
    return (
      <div className="presenter-dock-source-picker">
        <span className="presenter-dock-label">Captions source</span>
        <div className="presenter-dock-source-options">
          {micSupported ? (
            <button
              className="meeting-btn meeting-btn-sm meeting-btn-ghost presenter-dock-icon-btn"
              onClick={onToggleMic}
              aria-pressed={false}
              aria-label="Capture audio with the microphone"
            >
              <MicIcon />
              Use mic
            </button>
          ) : (
            <button
              className="meeting-btn meeting-btn-sm meeting-btn-ghost presenter-dock-icon-btn"
              disabled
              title="Speech recognition is not supported in this browser"
            >
              <MicIcon />
              Use mic
            </button>
          )}
          <button
            className="meeting-btn meeting-btn-sm meeting-btn-ghost"
            onClick={() => setSource('teams')}
          >
            Join Teams…
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="presenter-dock">
      <div className="presenter-dock-row">
        <div className="presenter-dock-group">
          <span className="presenter-dock-label">Broadcast</span>
          <span className="presenter-dock-state presenter-dock-live">
            <span className="presenter-dock-dot presenter-dock-dot-live" aria-hidden="true" />
            LIVE
          </span>
        </div>
        <div className="presenter-dock-source">{renderSource()}</div>
        <div className="presenter-dock-spacer" />
        <button
          className="meeting-btn meeting-btn-secondary presenter-dock-stop presenter-dock-danger presenter-dock-icon-btn"
          disabled={stoppingLive}
          onClick={onStopLive}
        >
          {stoppingLive ? (
            'Stopping Live…'
          ) : (
            <>
              <StopIcon size={13} />
              Stop Live
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PresenterDock;
